import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Menu, X, Mail } from "lucide-react";
import LanguageSelector from "./LanguageSelector";

const NAV_ITEMS = [
  { id: "metoden", key: "nav.metoden" },
  { id: "coach", key: "nav.coach" },
  { id: "historien", key: "nav.historien" },
];

// Mobile menu keeps the full story-level navigation
const MOBILE_NAV_ITEMS = [
  { id: "metoden", key: "nav.metoden" },
  { id: "biologi", key: "nav.biologi" },
  { id: "banen", key: "nav.banen" },
  { id: "coach", key: "nav.coach" },
  { id: "historien", key: "nav.historien" },
  { id: "udtalelser", key: "nav.udtalelser" },
  { id: "videnskab", key: "nav.videnskab" },
  { id: "kontakt", key: "nav.kontakt" },
];

const Navbar = () => {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setOpen(false);
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled ? "nav-blur py-3" : "py-5"
      }`}
      style={{
        // Respect iPhone notch + Android display cutouts on touch devices
        paddingTop: "calc(env(safe-area-inset-top, 0px) + 0.5rem)",
      }}
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-between px-5 md:px-10">
        {/* Subtle left-side brand presence — small text-only wordmark, two
            stacked lines.  Calm, premium, cinematic.  Clicking it returns
            to the top of the page. */}
        <button
          data-testid="brand-wordmark"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          aria-label="EleKtro Universal — Future Football™"
          className="flex flex-col items-start leading-none select-none focus:outline-none"
        >
          <span
            className="font-display font-bold uppercase tracking-[0.06em] text-white text-[13px] md:text-[15px] whitespace-nowrap"
            style={{
              textShadow:
                "0 0 14px rgba(255,255,255,0.35), 0 0 4px rgba(255,255,255,0.6)",
            }}
          >
            EleKtro Universal
          </span>
          <span
            className="mt-1.5 font-display uppercase font-medium tracking-[0.32em] text-[9px] md:text-[10px] whitespace-nowrap"
            style={{
              color: "#6FF6FF",
              textShadow:
                "0 0 8px rgba(111,246,255,0.85), 0 0 18px rgba(61,191,255,0.45)",
            }}
          >
            Future Football™
          </span>
        </button>

        {/* Desktop nav — minimal 3 premium items; full story stays in mobile drawer */}
        <nav className="hidden items-center gap-10 xl:gap-14 lg:flex" aria-label="Primary">
          {NAV_ITEMS.map((item) => (
            <button
              key={item.id}
              data-testid={`nav-link-${item.id}`}
              onClick={() => scrollTo(item.id)}
              className="whitespace-nowrap text-[11px] font-medium uppercase tracking-[0.24em] text-white/70 transition-colors hover:text-white"
            >
              {t(item.key)}
            </button>
          ))}
        </nav>

        {/* Right cluster */}
        <div className="flex items-center gap-3">
          <LanguageSelector />
          <button
            data-testid="navbar-cta"
            onClick={() => scrollTo("kontakt")}
            className="hidden md:inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[10.5px] uppercase tracking-[0.18em] btn-neon-ghost"
          >
            <Mail className="h-3 w-3" />
            {t("nav.book_cta")}
          </button>
          <button
            data-testid="navbar-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-white"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden border-t border-white/10 nav-blur"
        >
          <nav className="flex flex-col px-5 py-5">
            {MOBILE_NAV_ITEMS.map((item) => (
              <button
                key={item.id}
                data-testid={`mobile-nav-${item.id}`}
                onClick={() => scrollTo(item.id)}
                className="border-b border-white/5 py-3 text-left text-sm uppercase tracking-[0.18em] text-white/75 hover:text-white"
              >
                {t(item.key)}
              </button>
            ))}
            <button
              data-testid="mobile-cta"
              onClick={() => scrollTo("kontakt")}
              className="mt-4 inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm btn-neon-primary"
            >
              <Mail className="h-4 w-4" />
              {t("nav.book_cta")}
            </button>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Navbar;
